import React, { useState, useEffect, Suspense } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import userService from '../../../services/UserService';
import appService from '../../../services/ApplicationService';
import OutlinedCard from '../../common/MediaCard';
import Separator from '../../layout/Separator';
import companyService from '../../../services/CompanyService';
import ErrorBoundary from '../../common/ErrorBoundery';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ReviewsRoundedIcon from '@mui/icons-material/ReviewsRounded';
import CustomSpeedDial from '../../common/SpeedDial';
import DynamicDrawer from '../../common/Drawer';
import NormalTextField from '../../common/NormalTextField';
import MultipleSelect from '../../common/MultipleSelect';
import auditService from '../../../services/AuditService';
import DynamicSnackbar from '../../common/Snackbar';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import Chip from '@mui/material/Chip';
import MovingRoundedIcon from '@mui/icons-material/MovingRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import HttpsRoundedIcon from '@mui/icons-material/HttpsRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import PauseCircleFilledRoundedIcon from '@mui/icons-material/PauseCircleFilledRounded';
import { format } from 'date-fns';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const AuditProjects = () => {

    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [apps, setApps] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [projectsData, setProjectsData] = useState([]);
    const [companyId, setCompanyId] = useState([]);
    const [auditType, setAuditType] = useState([]);
    const [status, setStatus] = useState([]);
    const [newData, setNewData] = useState([]);
    const [projectExist, setProjectExist] = useState([]);
    const [errors, setErrors] = useState({
        COMPANY_ID: '',
        AUDIT_ID: '',
        AUDIT_NAME: '',
        AUDIT_DESCRIPTION: '',
    });

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const [recentProjects, setRecentProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [accessError, setAccessError] = useState(null);

    const [isFormValid, setFormValid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOption, setSortOption] = useState('recent');
    const [searchQuery, setSearchQuery] = useState('');
    const [companyMap, setCompanyMap] = useState({});
    const [filterValues, setFilterValues] = useState({
        auditType: '',
        status: '',
        company: ''
    });

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Load recent projects from localStorage on component mount
    useEffect(() => {
        const storedRecentProjects = localStorage.getItem('recentAuditProjects');
        if (storedRecentProjects) {
            setRecentProjects(JSON.parse(storedRecentProjects));
        }
    }, []);

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const companyRecord = await companyService.fetchCompanies()

                if (companyRecord) {
                    // Create a map of company IDs to company names for quick lookup
                    const companyMapping = {};
                    companyRecord.forEach(company => {
                        companyMapping[company.id] = company.COMPANY_NAME;
                    });
                    setCompanyMap(companyMapping);
                    
                    const company = companyRecord.map(item => ({
                        value: item.id,
                        label: item.COMPANY_NAME,
                    }));
                    setCompanies(company);
                }
            } catch (fetchError) {
                console.error('Error fetching company:', fetchError);
                setCompanyId([]);
            }
        }

        const fetchProjects = async () => {
            try {
                setIsLoading(true);
                const projectRecord = await auditService.fetchProjects()

                if (projectRecord) {
                    // Get stored recent project IDs
                    const storedRecentProjects = JSON.parse(localStorage.getItem('recentAuditProjects') || '[]');
                    
                    // Update stored projects with latest data
                    const updatedRecentProjects = storedRecentProjects.map(recentProject => {
                        const latestData = projectRecord.find(p => p.id === recentProject.id);
                        return latestData || recentProject;
                    }).filter(project => projectRecord.some(p => p.id === project.id)); // Remove deleted projects
                    
                    // Sort all projects by last modified
                    const sortedProjects = [...projectRecord].sort((a, b) => 
                        new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
                    );
                    
                    setProjectsData(sortedProjects);
                    setProjectExist(projectRecord);
                    setRecentProjects(updatedRecentProjects);
                    
                    // Update localStorage
                    localStorage.setItem('recentAuditProjects', JSON.stringify(updatedRecentProjects));
                } else {
                    setProjectsData([]);
                    setProjectExist([]);
                    setRecentProjects([]);
                    localStorage.removeItem('recentAuditProjects');
                }
            } catch (error) {
                console.error('Error fetching projects:', error);
                if (error.response && error.response.status === 403) {
                    setAccessError("You don't have access to any audit projects. Please contact your administrator.");
                } else {
                    setAccessError("An error occurred while fetching projects. Please try again later.");
                }
                setProjectsData([]);
                setRecentProjects([]);
            } finally {
                setIsLoading(false);
            }
        }

        fetchProjects()
        fetchCompany()
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        const updatedData = {
            ...newData,
            [name]: value
        };

        setNewData(updatedData);
        validateFormFields(updatedData);
    };

    const handleDateChange = (newValue) => {
        // Format date as YYYY-MM-DD string
        let formattedDate = '';
        
        if (newValue) {
            // For handling both dayjs and regular Date objects
            const date = newValue instanceof Date ? newValue : new Date(newValue);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            formattedDate = `${year}-${month}-${day}`;
        }

        const updatedData = {
            ...newData,
            PERIOD_END_DATE: formattedDate
        };

        setNewData(updatedData);
        validateFormFields(updatedData);
    };

    const handleControlBlur = (e) => {
        const { name, value } = e.target;

        const updatedData = {
            ...newData,
            [name]: value
        };

        setNewData(updatedData);

        const newErrors = { ...errors };

        if (name === 'AUDIT_ID') {
            if (!value) {
                newErrors.AUDIT_ID = 'This field is required';
            } else if (projectExist.some(project => project.AUDIT_ID === value)) {
                newErrors.AUDIT_ID = 'Audit ID already exists';
            } else {
                newErrors.AUDIT_ID = '';
            }
        } else if (name === 'AUDIT_NAME') {
            if (!value) {
                newErrors.AUDIT_NAME = 'This field is required';
            } else {
                newErrors.AUDIT_NAME = '';
            }
        } else if (name === 'AUDIT_DESCRIPTION') {
            if (!value) {
                newErrors.AUDIT_DESCRIPTION = 'This field is required';
            } else {
                newErrors.AUDIT_DESCRIPTION = '';
            }
        }
        
        setErrors(newErrors);
        validateFormFields(updatedData);
    };

    const toggleDrawer = (open) => () => {
        if (open) {
            // Initialize form fields when opening the drawer
            setNewData({});
            setCompanyId(null);
            setAuditType(null);
            setStatus(null);
            setErrors({
                COMPANY_ID: '',
                AUDIT_ID: '',
                AUDIT_NAME: '',
                AUDIT_DESCRIPTION: '',
            });
            setFormValid(false);
        } else {
            // Clear form fields when closing the drawer
            setNewData({});
            setCompanyId(null);
            setAuditType(null);
            setStatus(null);
            setErrors({
                COMPANY_ID: '',
                AUDIT_ID: '',
                AUDIT_NAME: '',
                AUDIT_DESCRIPTION: '',
            });
            setFormValid(false);
        }
        setOpen(open);
    };

    const handleCompanyIDChange = (selectedType) => {
        if (!selectedType) {
            return;
        }
        
        const companyId = selectedType.value;
        setCompanyId(selectedType);

        const updatedData = {
            ...newData,
            COMPANY_ID: companyId
        };
        setNewData(updatedData);
        validateFormFields(updatedData);
    };

    const handleAuditTypeChange = (selectedType) => {
        const audittype = selectedType.value
        setAuditType(selectedType)

        const updatedData = {
            ...newData,
            AUDIT_TYPE: audittype
        };
        setNewData(updatedData);
    };

    const handleStatusChange = (selectedType) => {
        const status = selectedType.value
        setStatus(selectedType)

        const updatedData = {
            ...newData,
            STATUS: status
        };
        setNewData(updatedData);

    };

    const statusOptions = [
        { value: 'Not Started', label: 'Not Started' },
        { value: 'Active', label: 'Active' },
        { value: 'Archived', label: 'Archived' },
        { value: 'On-Hold', label: 'On-Hold' },
    ];

    const auditTypeOptions = [
        { value: 'SOX Audit', label: 'SOX Audit' },
        { value: 'Operational Audit', label: 'Operational Audit' },
        { value: 'Compliance Audit', label: 'Compliance Audit' },
        { value: 'Fraud Audit', label: 'Fraud Audit' },
        { value: 'Regulatory Audit', label: 'Regulatory Audit' },
        { value: 'ISO Audit', label: 'ISO Audit' },
        { value: 'Other Audit', label: 'Other Audit' },
    ];

    // Helper method to validate all form fields together
    const validateFormFields = (data) => {
        const hasRequiredFields = !!data.AUDIT_ID && 
                                !!data.AUDIT_NAME && 
                                !!data.AUDIT_DESCRIPTION;
        
        const hasNoErrors = !errors.AUDIT_ID && 
                           !errors.AUDIT_NAME && 
                           !errors.AUDIT_DESCRIPTION;
        
        setFormValid(hasRequiredFields && hasNoErrors);
    };

    const createProject = async (e) => {
        e.preventDefault();
        
        if (!isFormValid) {
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            const newProject = await auditService.createProjects(newData);
            if (newProject) {
                setSnackbarMessage('Successfully created project');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                
                // Close the drawer
                toggleDrawer(false)();
                
                setTimeout(() => {
                    window.location.reload();
                }, 300);
            }
        } catch (error) {
            console.error('Error creating project', error);
            setSnackbarMessage('There was a problem creating the project');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const drawerContent = (
        <mui.Box sx={{ 
            padding: '24px',
            width: '400px',
            maxWidth: '100%'
        }}>
            <mui.Typography variant="h6" sx={{ 
                mb: 3,
                fontWeight: 600, 
                color: 'text.primary',
                position: 'relative',
                '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '40px',
                    height: '3px',
                    backgroundColor: 'primary.main'
                }
            }}>
                Create New Audit Project
            </mui.Typography>

            <mui.Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
                <mui.Box sx={{ mb: 2.5 }}>
                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                        Company Name <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                    </mui.Typography>
                    <MultipleSelect
                        error={!!errors.COMPANY_ID}
                        isMultiSelect={false}
                        defaultValue={companyId ? companyId : ''}
                        placeholderText="Select company"
                        selectOptions={companies}
                        selectedOptions={companyId ? companyId : ''}
                        handleChange={handleCompanyIDChange}
                        helperText={errors.COMPANY_ID || ' '}
                        customStyles={{
                            control: (base) => ({
                                ...base,
                                minHeight: '40px',
                                height: '40px',
                                fontSize: '0.875rem'
                            })
                        }}
                    />
                </mui.Box>

                <mui.Box sx={{ mb: 2.5 }}>
                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                        Audit ID <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                    </mui.Typography>
                    <NormalTextField
                        error={!!errors.AUDIT_ID}
                        name="AUDIT_ID"
                        placeholder="Enter audit identifier"
                        value={newData.AUDIT_ID || ''}
                        onChange={handleChange}
                        onBlur={handleControlBlur}
                        helperText={errors.AUDIT_ID || ' '}
                        fullWidth
                        size="small"
                        sx={{
                            mt: 0,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                                height: '40px',
                            }
                        }}
                    />
                </mui.Box>

                <mui.Box sx={{ mb: 2.5 }}>
                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                        Audit Name <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                    </mui.Typography>
                    <NormalTextField
                        error={!!errors.AUDIT_NAME}
                        name="AUDIT_NAME"
                        placeholder="Enter descriptive name"
                        value={newData.AUDIT_NAME || ''}
                        onChange={handleChange}
                        onBlur={handleControlBlur}
                        helperText={errors.AUDIT_NAME || ' '}
                        fullWidth
                        size="small"
                        sx={{
                            mt: 0,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                                height: '40px',
                            }
                        }}
                    />
                </mui.Box>

                <mui.Box sx={{ mb: 2.5 }}>
                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                        Audit Description <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                    </mui.Typography>
                    <NormalTextField
                        error={!!errors.AUDIT_DESCRIPTION}
                        name="AUDIT_DESCRIPTION"
                        placeholder="Enter detailed description of the audit"
                        value={newData.AUDIT_DESCRIPTION || ''}
                        onChange={handleChange}
                        onBlur={handleControlBlur}
                        isMultiLine={true}
                        rows="4"
                        helperText={errors.AUDIT_DESCRIPTION || ' '}
                        fullWidth
                        sx={{
                            mt: 0,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                            }
                        }}
                    />
                </mui.Box>

                <mui.Box sx={{ mb: 2.5 }}>
                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                        Audit Type
                    </mui.Typography>
                    <MultipleSelect
                        isMultiSelect={false}
                        defaultValue={auditType ? auditType : ''}
                        placeholderText="Select type"
                        selectOptions={auditTypeOptions}
                        selectedOptions={auditType ? auditType : ''}
                        handleChange={handleAuditTypeChange}
                        customStyles={{
                            control: (base) => ({
                                ...base,
                                minHeight: '40px',
                                height: '40px',
                                fontSize: '0.875rem'
                            })
                        }}
                    />
                </mui.Box>

                <mui.Box sx={{ mb: 2.5 }}>
                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                        Period End Date
                    </mui.Typography>
                    <mui.TextField
                        type="date"
                        name="PERIOD_END_DATE"
                        value={newData.PERIOD_END_DATE || ''}
                        onChange={(e) => handleDateChange(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                                height: '40px',
                            }
                        }}
                    />
                </mui.Box>

                <mui.Box sx={{ mb: 2.5 }}>
                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                        Audit Status
                    </mui.Typography>
                    <MultipleSelect
                        isMultiSelect={false}
                        defaultValue={status ? status : ''}
                        placeholderText="Select status"
                        selectOptions={statusOptions}
                        selectedOptions={status ? status : ''}
                        handleChange={handleStatusChange}
                        customStyles={{
                            control: (base) => ({
                                ...base,
                                minHeight: '40px',
                                height: '40px',
                                fontSize: '0.875rem'
                            })
                        }}
                    />
                </mui.Box>

                <mui.Button 
                    variant="contained" 
                    onClick={createProject}
                    fullWidth
                    disabled={!isFormValid || isSubmitting}
                    startIcon={isSubmitting ? <mui.CircularProgress size={16} color="inherit" /> : null}
                    sx={{
                        py: 1.5,
                        mt: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        backgroundColor: 'primary.main',
                        '&:hover': {
                            backgroundColor: 'primary.dark',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        },
                        '&:disabled': {
                            backgroundColor: isSubmitting ? 'primary.main' : 'rgba(0, 0, 0, 0.12)',
                            color: isSubmitting ? 'white' : 'rgba(0, 0, 0, 0.26)',
                            opacity: isSubmitting ? 0.7 : 1
                        }
                    }}
                >
                    {isSubmitting ? 'Creating...' : 'Create Project'}
                </mui.Button>
            </mui.Box>
        </mui.Box>
    );

    const projectSettings = (projectId) => {
        const clickedProject = projectsData.find(p => p.id === projectId) || 
                             recentProjects.find(p => p.id === projectId);
        
        if (clickedProject) {
            // Update the project with current timestamp
            const updatedProject = {
                ...clickedProject,
                LAST_ACCESSED: new Date().toISOString()
            };

            // Update recent projects list
            const updatedRecentProjects = [
                updatedProject,
                ...recentProjects.filter(p => p.id !== projectId)
            ].slice(0, 3); // Keep only the 3 most recent

            // Update localStorage
            localStorage.setItem('recentAuditProjects', JSON.stringify(updatedRecentProjects));
            
            // Update state
            setRecentProjects(updatedRecentProjects);
            setProjectsData(prevProjects => 
                prevProjects.map(project => 
                    project.id === projectId 
                        ? updatedProject
                        : project
                )
            );
            
            // Navigate to project settings
            navigate(`/Audit/Projects/${projectId}`);
        }
    }

    // Helper function to get company name from ID
    const getCompanyName = (companyId) => {
        return companyMap[companyId] || 'Unknown Company';
    };

    // Filter and sort projects
    const getFilteredProjects = () => {
        let filtered = [...projectsData];
        
        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(project => 
                (project.AUDIT_NAME && project.AUDIT_NAME.toLowerCase().includes(query)) ||
                (project.AUDIT_ID && project.AUDIT_ID.toLowerCase().includes(query)) ||
                (project.AUDIT_TYPE && project.AUDIT_TYPE.toLowerCase().includes(query)) ||
                (getCompanyName(project.COMPANY_ID) && getCompanyName(project.COMPANY_ID).toLowerCase().includes(query))
            );
        }
        
        // Apply additional filters
        if (filterValues.auditType) {
            filtered = filtered.filter(project => project.AUDIT_TYPE === filterValues.auditType);
        }
        
        if (filterValues.status) {
            filtered = filtered.filter(project => project.STATUS === filterValues.status);
        }
        
        if (filterValues.company) {
            filtered = filtered.filter(project => project.COMPANY_ID === filterValues.company);
        }
        
        // Apply sorting
        switch (sortOption) {
            case 'recent':
                filtered.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
                break;
            case 'name':
                filtered.sort((a, b) => (a.AUDIT_NAME || '').localeCompare(b.AUDIT_NAME || ''));
                break;
            case 'type':
                filtered.sort((a, b) => (a.AUDIT_TYPE || '').localeCompare(b.AUDIT_TYPE || ''));
                break;
            case 'company':
                filtered.sort((a, b) => getCompanyName(a.COMPANY_ID).localeCompare(getCompanyName(b.COMPANY_ID)));
                break;
            case 'status':
                filtered.sort((a, b) => (a.STATUS || '').localeCompare(b.STATUS || ''));
                break;
            default:
                // Default sort by recent
                filtered.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
        }
        
        return filtered;
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const toggleFilters = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    const handleFilterChange = (filterName, value) => {
        setFilterValues(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const clearFilters = () => {
        setFilterValues({
            auditType: '',
            status: '',
            company: ''
        });
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Typography color="text.primary">Audit Projects</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                        <ReviewsRoundedIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                Audit Projects
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Create and manage your audit projects. Track progress, set milestones, and ensure compliance across all your auditing activities.
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    {isLoading ? (
                        <mui.Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            minHeight: '200px' 
                        }}>
                            <mui.CircularProgress />
                        </mui.Box>
                    ) : accessError ? (
                        <mui.Paper 
                            elevation={0} 
                            sx={{ 
                                p: 3, 
                                textAlign: 'center',
                                backgroundColor: '#fff3cd',
                                color: '#856404',
                                border: '1px solid #ffeeba',
                                borderRadius: 2,
                                mt: 3
                            }}
                        >
                            <mui.Typography variant="h6" component="p">
                                {accessError}
                            </mui.Typography>
                        </mui.Paper>
                    ) : (
                        <>
                            {recentProjects.length > 0 && (
                                <mui.Box sx={{ mb: 3 }}>
                                    <mui.Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        mb: 2,
                                        gap: 0.75
                                    }}>
                                        <AutoAwesomeRoundedIcon sx={{ 
                                            fontSize: 18,
                                            color: 'primary.main',
                                            opacity: 0.8
                                        }} />
                                        <mui.Typography variant="subtitle1" sx={{ 
                                            fontWeight: 600, 
                                            color: 'text.primary',
                                            fontSize: '0.95rem'
                                        }}>
                                            Recently Accessed
                                        </mui.Typography>
                                    </mui.Box>
                                    <mui.Grid container spacing={2}>
                                        {recentProjects.map((project) => (
                                            <mui.Grid item xs={12} sm={6} md={4} key={`recent-${project.id}`}>
                                                <mui.Paper
                                                    component={mui.Link}
                                                    onClick={() => projectSettings(project.id)}
                                                    sx={{
                                                        p: 2,
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        borderRadius: 1.5,
                                                        backgroundColor: '#ffffff',
                                                        textDecoration: 'none',
                                                        cursor: 'pointer',
                                                        position: 'relative',
                                                        overflow: 'hidden',
                                                        transition: 'all 0.2s ease-in-out',
                                                        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
                                                        border: '1px solid',
                                                        borderColor: 'rgba(0, 0, 0, 0.08)',
                                                        '&:hover': {
                                                            backgroundColor: '#ffffff',
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                            borderColor: 'rgba(25, 118, 210, 0.2)'
                                                        }
                                                    }}
                                                >
                                                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <span style={{ 
                                                                width: 6,
                                                                height: 6,
                                                                borderRadius: '50%',
                                                                backgroundColor: project.STATUS === 'Active' ? '#10b981' : 
                                                                              project.STATUS === 'Archived' ? '#6b7280' :
                                                                              project.STATUS === 'On-Hold' ? '#ef4444' : '#f59e0b',
                                                                opacity: 0.8,
                                                                marginRight: '8px'
                                                            }} />
                                                            <mui.Typography 
                                                                variant="body2" 
                                                                sx={{ 
                                                                    color: 'text.secondary',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 500
                                                                }}
                                                            >
                                                                {project.STATUS}
                                                            </mui.Typography>
                                                        </mui.Box>
                                                        <mui.Chip
                                                            label={getCompanyName(project.COMPANY_ID)}
                                                            size="small"
                                                            sx={{
                                                                height: '20px',
                                                                fontSize: '0.68rem',
                                                                fontWeight: 600,
                                                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                                color: 'primary.main',
                                                                '& .MuiChip-label': {
                                                                    px: 1
                                                                }
                                                            }}
                                                        />
                                                    </mui.Box>
                                                    <mui.Typography 
                                                        variant="subtitle1" 
                                                        sx={{ 
                                                            fontWeight: 600,
                                                            color: 'text.primary',
                                                            mb: 0.75,
                                                            fontSize: '0.875rem',
                                                            lineHeight: 1.4
                                                        }}
                                                    >
                                                        {project.AUDIT_NAME}
                                                    </mui.Typography>
                                                    <mui.Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            fontSize: '0.8rem',
                                                            mb: 'auto',
                                                            opacity: 0.8
                                                        }}
                                                    >
                                                        {project.AUDIT_TYPE}
                                                    </mui.Typography>
                                                    
                                                    <mui.Box 
                                                        sx={{ 
                                                            mt: 2,
                                                            pt: 2,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            color: 'primary.main',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                            borderTop: '1px solid',
                                                            borderColor: 'rgba(0, 0, 0, 0.06)'
                                                        }}
                                                    >
                                                        View Details
                                                        <mui.Box 
                                                            component="span" 
                                                            sx={{ 
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                transition: 'transform 0.2s ease-in-out',
                                                                transform: 'translateX(0)',
                                                                '.MuiPaper-root:hover &': {
                                                                    transform: 'translateX(4px)'
                                                                }
                                                            }}
                                                        >
                                                            <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
                                                        </mui.Box>
                                                    </mui.Box>
                                                </mui.Paper>
                                            </mui.Grid>
                                        ))}
                                    </mui.Grid>
                                </mui.Box>
                            )}

                            <mui.Box sx={{ 
                                backgroundColor: '#f8fafc', 
                                borderRadius: 3,
                                minHeight: '80vh',
                                boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)'
                            }}>
                                <mui.Paper 
                                    elevation={0}
                                    sx={{ 
                                        p: 3,
                                        height: '100%',
                                        backgroundColor: 'transparent'
                                    }}
                                >
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <mui.Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                            All Projects
                                        </mui.Typography>
                                        
                                        <mui.Box sx={{ display: 'flex', gap: 2 }}>
                                            <mui.TextField
                                                placeholder="Search projects..."
                                                size="small"
                                                value={searchQuery}
                                                onChange={handleSearchChange}
                                                InputProps={{
                                                    startAdornment: (
                                                        <mui.InputAdornment position="start">
                                                            <mui.SvgIcon fontSize="small" color="action">
                                                                <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                                                            </mui.SvgIcon>
                                                        </mui.InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    width: '250px',
                                                    backgroundColor: 'white',
                                                    borderRadius: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 1,
                                                        borderColor: 'rgba(0, 0, 0, 0.08)'
                                                    }
                                                }}
                                            />
                                            
                                            <mui.FormControl sx={{ minWidth: 120, backgroundColor: 'white', borderRadius: 1 }} size="small">
                                                <mui.InputLabel id="sort-select-label">Sort By</mui.InputLabel>
                                                <mui.Select
                                                    labelId="sort-select-label"
                                                    id="sort-select"
                                                    value={sortOption}
                                                    label="Sort By"
                                                    onChange={(e) => setSortOption(e.target.value)}
                                                >
                                                    <mui.MenuItem value="recent">Most Recent</mui.MenuItem>
                                                    <mui.MenuItem value="name">Name</mui.MenuItem>
                                                    <mui.MenuItem value="type">Type</mui.MenuItem>
                                                    <mui.MenuItem value="company">Company</mui.MenuItem>
                                                    <mui.MenuItem value="status">Status</mui.MenuItem>
                                                </mui.Select>
                                            </mui.FormControl>
                                            
                                            <mui.Button
                                                variant="outlined"
                                                startIcon={<mui.Icon>filter_list</mui.Icon>}
                                                onClick={toggleFilters}
                                                sx={{
                                                    borderColor: 'rgba(0, 0, 0, 0.12)',
                                                    color: 'text.primary',
                                                    backgroundColor: 'white',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                    }
                                                }}
                                            >
                                                Filters
                                            </mui.Button>
                                        </mui.Box>
                                    </mui.Box>
                                    
                                    {isFilterOpen && (
                                        <mui.Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                mb: 3,
                                                borderRadius: 2,
                                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                                backgroundColor: 'white'
                                            }}
                                        >
                                            <mui.Grid container spacing={2}>
                                                <mui.Grid item xs={12} md={3}>
                                                    <mui.FormControl fullWidth size="small">
                                                        <mui.InputLabel>Audit Type</mui.InputLabel>
                                                        <mui.Select
                                                            value={filterValues.auditType}
                                                            label="Audit Type"
                                                            onChange={(e) => handleFilterChange('auditType', e.target.value)}
                                                        >
                                                            <mui.MenuItem value="">
                                                                <em>Any</em>
                                                            </mui.MenuItem>
                                                            {auditTypeOptions.map(option => (
                                                                <mui.MenuItem key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </mui.MenuItem>
                                                            ))}
                                                        </mui.Select>
                                                    </mui.FormControl>
                                                </mui.Grid>
                                                <mui.Grid item xs={12} md={3}>
                                                    <mui.FormControl fullWidth size="small">
                                                        <mui.InputLabel>Status</mui.InputLabel>
                                                        <mui.Select
                                                            value={filterValues.status}
                                                            label="Status"
                                                            onChange={(e) => handleFilterChange('status', e.target.value)}
                                                        >
                                                            <mui.MenuItem value="">
                                                                <em>Any</em>
                                                            </mui.MenuItem>
                                                            {statusOptions.map(option => (
                                                                <mui.MenuItem key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </mui.MenuItem>
                                                            ))}
                                                        </mui.Select>
                                                    </mui.FormControl>
                                                </mui.Grid>
                                                <mui.Grid item xs={12} md={3}>
                                                    <mui.FormControl fullWidth size="small">
                                                        <mui.InputLabel>Company</mui.InputLabel>
                                                        <mui.Select
                                                            value={filterValues.company}
                                                            label="Company"
                                                            onChange={(e) => handleFilterChange('company', e.target.value)}
                                                        >
                                                            <mui.MenuItem value="">
                                                                <em>Any</em>
                                                            </mui.MenuItem>
                                                            {companies.map(company => (
                                                                <mui.MenuItem key={company.value} value={company.value}>
                                                                    {company.label}
                                                                </mui.MenuItem>
                                                            ))}
                                                        </mui.Select>
                                                    </mui.FormControl>
                                                </mui.Grid>
                                                <mui.Grid item xs={12} md={3}>
                                                    <mui.Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        <mui.Button
                                                            variant="outlined"
                                                            color="inherit"
                                                            size="small"
                                                            onClick={clearFilters}
                                                            sx={{ textTransform: 'none' }}
                                                        >
                                                            Clear
                                                        </mui.Button>
                                                    </mui.Box>
                                                </mui.Grid>
                                            </mui.Grid>
                                        </mui.Paper>
                                    )}
                                    
                                    {getFilteredProjects().map((project) => (
                                        <mui.Paper 
                                            key={project.id}
                                            elevation={0}
                                            sx={{ 
                                                p: 2.5, 
                                                mb: 2,
                                                borderRadius: 2,
                                                backgroundColor: '#ffffff',
                                                transition: 'all 0.2s ease-in-out',
                                                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                                }
                                            }}
                                        >
                                            <mui.Grid
                                                container 
                                                spacing={3}
                                                alignItems="center"
                                            >
                                                <mui.Grid item xs={12} md={3}>
                                                    <mui.Typography 
                                                        variant="subtitle1" 
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: 'text.primary',
                                                            mb: 0.5,
                                                            fontSize: '0.95rem'
                                                        }}
                                                    >
                                                        {project.AUDIT_NAME || 'N/A'}
                                                    </mui.Typography>
                                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <mui.Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                color: 'text.secondary',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                fontSize: '0.8rem'
                                                            }}
                                                        >
                                                            <span style={{ 
                                                                width: 8, 
                                                                height: 8, 
                                                                borderRadius: '50%',
                                                                backgroundColor: project.STATUS === 'Active' ? '#10b981' : 
                                                                              project.STATUS === 'Archived' ? '#6b7280' :
                                                                              project.STATUS === 'On-Hold' ? '#ef4444' : '#f59e0b'
                                                            }} />
                                                            {project.STATUS || 'N/A'}
                                                        </mui.Typography>
                                                        <mui.Chip
                                                            label={getCompanyName(project.COMPANY_ID)}
                                                            size="small"
                                                            sx={{
                                                                height: '20px',
                                                                fontSize: '0.68rem',
                                                                fontWeight: 600,
                                                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                                color: 'primary.main',
                                                                '& .MuiChip-label': {
                                                                    px: 1
                                                                }
                                                            }}
                                                        />
                                                    </mui.Box>
                                                </mui.Grid>
                                              
                                                <mui.Grid item xs={12} md={2}>
                                                    <mui.Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            fontSize: '0.8rem',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        Type
                                                    </mui.Typography>
                                                    <mui.Typography 
                                                        variant="body1" 
                                                        sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {project.AUDIT_TYPE || 'N/A'}
                                                    </mui.Typography>
                                                </mui.Grid>

                                                <mui.Grid item xs={12} md={2}>
                                                    <mui.Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            fontSize: '0.8rem',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        Period End Date
                                                    </mui.Typography>
                                                    <mui.Typography 
                                                        variant="body1" 
                                                        sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {project.PERIOD_END_DATE
                                                            ? format(new Date(project.PERIOD_END_DATE), 'MMM dd, yyyy')
                                                            : 'N/A'}
                                                    </mui.Typography>
                                                </mui.Grid>

                                                <mui.Grid item xs={12} md={4}>
                                                    <mui.Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            fontSize: '0.8rem',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        Details
                                                    </mui.Typography>
                                                    <mui.Typography 
                                                        variant="body1" 
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            lineHeight: 1.5,
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {project.AUDIT_DESCRIPTION || 'N/A'}
                                                    </mui.Typography>
                                                </mui.Grid>

                                                <mui.Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <mui.IconButton 
                                                        onClick={() => projectSettings(project.id)}
                                                        sx={{ 
                                                            color: 'primary.main',
                                                            backgroundColor: 'transparent',
                                                            '&:hover': {
                                                                backgroundColor: 'action.hover'
                                                            }
                                                        }}
                                                    >
                                                        <OpenInNewIcon sx={{ fontSize: 20 }} />
                                                    </mui.IconButton>
                                                </mui.Grid>
                                            </mui.Grid>
                                        </mui.Paper>
                                    ))}
                                    
                                    {getFilteredProjects().length === 0 && (
                                        <mui.Box 
                                            sx={{ 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                backgroundColor: 'white',
                                                borderRadius: 2,
                                                py: 5,
                                                px: 3,
                                                mt: 3
                                            }}
                                        >
                                            <mui.SvgIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.5, mb: 1 }}>
                                                <path d="M11 15h2v2h-2v-2zm0-8h2v6h-2V7zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                                            </mui.SvgIcon>
                                            <mui.Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                                                No projects found
                                            </mui.Typography>
                                            <mui.Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                                Try adjusting your search or filter criteria
                                            </mui.Typography>
                                        </mui.Box>
                                    )}
                                </mui.Paper>
                            </mui.Box>
                        </>
                    )}

                    {/* Use the CustomSpeedDial with actions like in ControlsLibrary */}
                    <CustomSpeedDial 
                        actions={[
                            { 
                                name: 'Create Project', 
                                icon: <ReviewsRoundedIcon />, 
                                onClick: () => toggleDrawer(true)() 
                            }
                        ]} 
                    />

                    <DynamicDrawer
                        open={open} 
                        onClose={toggleDrawer(false)}
                    >
                        {drawerContent}
                    </DynamicDrawer>

                    <DynamicSnackbar
                        open={snackbarOpen}
                        message={snackbarMessage}
                        severity={snackbarSeverity}
                        onClose={handleSnackbarClose}
                    />
                </mui.Box>
            </ResponsiveContainer>
        </div>
    )

    return (
        <ErrorBoundary fallback={<p>Failed to load data.</p>} error={error}>
            <Suspense fallback="Loading...">
                <div>
                    <IASideBar mainContent={customMainContent} />
                </div>
            </Suspense>
        </ErrorBoundary>
    );
}
export default AuditProjects;